/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import {
  getLocator,
  getProductUrlForDAM,
  mergeUrlQueryParams,
  isCanada,
  isClient,
  getIconPath,
  getDynamicBadgeQty,
  getAPIConfig,
  getBackGroundStyle,
} from '@tcp/core/src/utils';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import DamImage from '@tcp/core/src/components/common/atoms/DamImage';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Image from '@tcp/core/src/components/common/atoms/Image';
import SafeComponent from '@tcp/core/src/components/common/hoc/withErrorBoundary';
import ColorSwatches from '@tcp/core/src/components/features/browse/PlpTile/views/ColorSwatches';
import RatingAndReviews from '@tcp/core/src/components/features/browse/BundleProduct/atoms/RatingsAndReviews';
import AddedtoBagDynamicStore from '@tcp/core/src/components/features/CnC/AddedToBag/container/AddedToBagDynamicStore.js';
import ProductTabListDynamicStore from '@tcp/core/src/components/common/organisms/ProductTabList/container/ProductTabListDynamicStore.js';
import { ProductPricesSection } from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/views/ProductItemComponents';
import { getProductListToPath } from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/utils/productsCommonUtils';
import Promotions from '@tcp/core/src/components/features/browse/BundleProduct/atoms/Promotions';
import {
  getImageAssetConfig,
  isBundleProduct,
} from '@tcp/core/src/components/features/browse/PlpTile/views/plpTileHelpers/plpTileCommonHelpers';
import {
  IMG_DATA_CLP,
  TOTAL_IMAGES,
  ARROW_VISIBLE_BP,
  SLIDES_TO_SCROLL_BP,
  calculateBreakPointByWidth,
  SLIDES_TO_SCROLL_BP_SIMILAR_STYLES,
  IMG_DATA_CLP_SIMILAR,
} from '../config/CarouselTab.config';
import Carousel from '../../Carousel';
import {
  StyledTabSection,
  StyledProductSection,
  StyledSkeleton,
  StyledProductName,
  StyledProduct,
  StyledPriceSection,
  StyledProductDetails,
  StyledRatingSection,
  StyledProductWrapper,
  ATBIcon,
  ColorSwatchWrapper,
} from '../styles/CarouselTab.style';
import LabelsContext from '../../../../../context/LabelsContext';

const ABVariation = 'ab_test';

export const getProductsData = ({ productData, categoryId, fetchRecommendationData }) => {
  if (!productData) {
    fetchRecommendationData({ categoryId, isTabbedModule: true });
  }
};

export const getCategoryIds = (catIds) => {
  const processedCatId = [];
  if (catIds.length) {
    catIds.forEach((item) => processedCatId.push(item.val || item));
  }
  return processedCatId;
};

export const getTabItemsMap = (tabItems) => {
  return tabItems?.reduce((map, item) => {
    const { category = [] } = item;

    const catId = category.map((cat) => cat.val).join('_');
    const tabsMap = map;
    tabsMap[catId] = item;
    return tabsMap;
  }, {});
};

const getButtonTabItems = (tabItems) => {
  return tabItems?.map((item) => {
    const {
      category = {},
      styled: { text, styledObjWeb },
      mediaLinkedList = {},
      singleCTAButton = {},
    } = item;
    const processedCatId = getCategoryIds(category);
    return {
      label: text,
      id: processedCatId,
      imageBlock: mediaLinkedList,
      cta: singleCTAButton,
      overrideStyle: styledObjWeb,
    };
  });
};

export const getActiveStatus = (id, selectedTabId = []) => {
  const selectedTab = Array.isArray(selectedTabId) ? selectedTabId[0] : selectedTabId;
  if (Array.isArray(id) && JSON.stringify(id) === JSON.stringify(selectedTabId)) {
    return true;
  }
  return id === selectedTab;
};

const handleClick = (e, uniqueId, onQuickViewOpenClick) => {
  e.preventDefault();
  onQuickViewOpenClick({
    colorProductId: uniqueId,
  });
};

export const updateCategoryId = async (
  categoryId,
  setCurrentCatId,
  carouselTabs,
  productTabList,
  getProductTabListData,
  onProductTabChange
) => {
  if (categoryId) {
    setCurrentCatId(categoryId);
    onProductTabChange(categoryId, getTabItemsMap(carouselTabs)[categoryId.join('_')]);
    await ProductTabListDynamicStore();
    categoryId.forEach((id) => {
      // eslint-disable-next-line dot-notation
      if (!productTabList[id] || !productTabList['tabbedModule']?.[id]) {
        getProductTabListData({ categoryId: id, isTabbedModule: true });
      }
    });
  }
};

const renderAddToBagButton = (uniqueId, name, onQuickViewOpenClick) => {
  return (
    <ATBIcon
      className="tab-added-to-bag"
      aria-label={`Add to bag ${name}`}
      onClick={(e) => handleClick(e, uniqueId, onQuickViewOpenClick)}
      data-locator={getLocator('global_addtocart_Button')}
    >
      <Image
        className="tab-atb-icon-image"
        src={getIconPath('add-to-bag-icon-blue')}
        aria-hidden={true}
        alt="Add to bag Icon"
      />
    </ATBIcon>
  );
};

const renderImage = (
  newPdpUrl,
  pdpAsPathWithTrackingParams,
  index,
  uniqueId,
  productName,
  isHomePage,
  primaryBrand,
  isDamImageV2Enabled,
  dynamicBadgeOverlayQty,
  tcpStyleType,
  moduleAnalyticsTag,
  isSimilarProducts,
  imageUrl,
  isPngImage,
  isTransparentImageSizeReduceEnabled,
  backGroundStyle
  // eslint-disable-next-line max-params
) => {
  const imageConfig = isSimilarProducts ? IMG_DATA_CLP_SIMILAR.imgConfig : IMG_DATA_CLP.imgConfig;
  const imageURL = isSimilarProducts ? imageUrl : getProductUrlForDAM(uniqueId);

  return (
    <StyledProduct
      className="tab-product-img"
      {...(!isEmpty(backGroundStyle) ? { style: backGroundStyle } : {})}
    >
      <Anchor
        className="image-link"
        to={newPdpUrl}
        asPath={pdpAsPathWithTrackingParams}
        dataLocator={`${getLocator('moduleTabbed_image_link')}${index}`}
      >
        <DamImage
          imgData={{
            url: imageURL,
            alt: productName,
          }}
          imgConfigs={imageConfig}
          isProductImage
          className="moduleTabbed_carousel_img"
          lazyLoad={false}
          isHomePage={isHomePage}
          itemBrand={primaryBrand}
          primaryBrand={primaryBrand}
          isDamImageV2Enabled={isDamImageV2Enabled}
          dynamicBadgeOverlayQty={dynamicBadgeOverlayQty}
          tcpStyleType={tcpStyleType}
          badgeConfig={IMG_DATA_CLP.badgeConfig}
          moduleAnalyticsTag={moduleAnalyticsTag}
          isTransparentImageSizeReduceEnabled={isTransparentImageSizeReduceEnabled}
          isPngImage={isPngImage}
        />
      </Anchor>
    </StyledProduct>
  );
};

const renderPriceSection = (
  minListPrice,
  minOfferPrice,
  badge3,
  badge3Text,
  uniqueId,
  name,
  onQuickViewOpenClick,
  isDiscountPriceDisabled,
  isTabbedATBDisabled
) => {
  return (
    <StyledPriceSection className="tab-product-price-section">
      <ProductPricesSection
        listPrice={minListPrice}
        offerPrice={minOfferPrice}
        noMerchantBadge={badge3}
        merchantTag={badge3Text}
        isTabbedModuleDiscountPriceDisabled={isDiscountPriceDisabled}
      />
      {!isTabbedATBDisabled && renderAddToBagButton(uniqueId, name, onQuickViewOpenClick)}
    </StyledPriceSection>
  );
};

const renderProductName = (newPdpUrl, pdpAsPathWithTrackingParams, productName) => {
  return (
    <StyledProductName className="tab-product-heading">
      <Anchor to={newPdpUrl} asPath={pdpAsPathWithTrackingParams} anchorVariation="primary">
        {productName}
      </Anchor>
    </StyledProductName>
  );
};

const renderRatings = (ratings, reviewsCount, isRatingViewDisabled) => {
  if (isRatingViewDisabled) {
    return null;
  }
  return (
    <StyledRatingSection className="tab-product-rating-section">
      <RatingAndReviews ratings={ratings} reviewsCount={reviewsCount} />
    </StyledRatingSection>
  );
};

const renderPromotions = ({
  promotionsData,
  TCPPromotionPrice,
  TCPPromotionID,
  isUpdatedPromotionsDisplayEnabled,
  serverTime,
}) => {
  return (
    <Promotions
      promotionsData={promotionsData}
      TCPPromotionPrice={TCPPromotionPrice}
      TCPPromotionID={TCPPromotionID}
      isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
      serverTime={serverTime}
      isFormRecommendationTile
    />
  );
};

const renderColorSwatches = ({
  productPartNo,
  TCPSwatches,
  onQuickViewOpenClick,
  assetHost,
  assetPath,
  swatchImg,
  tcpColor,
  auxDescription,
  relatedSwatchImages,
  isBundle,
  isNewSwatchUrlEnabled,
  isColorSwatchesEnabled,
}) => {
  const relatedSwatches = (relatedSwatchImages && relatedSwatchImages.split(',')) || [];
  return isColorSwatchesEnabled ? (
    <ColorSwatchWrapper className="tabbed-carousel-color-swatch-wrapper">
      <ColorSwatches
        productPartNo={productPartNo}
        TCPSwatches={TCPSwatches}
        onQuickViewOpenClick={onQuickViewOpenClick}
        assetHost={assetHost}
        assetPath={assetPath}
        swatchImg={swatchImg}
        tcpColor={tcpColor}
        auxDescription={auxDescription}
        relatedSwatchImages={relatedSwatches}
        isBundleProduct={isBundle}
        handleClickATBIcon={handleClick}
        AddedtoBagDynamicStore={AddedtoBagDynamicStore}
        isNewSwatchUrlEnabled={isNewSwatchUrlEnabled}
        isTileRedesignAB={'variationA'}
      />
    </ColorSwatchWrapper>
  ) : (
    <></>
  );
};

const getBrandVal = (TCPPrimaryBrandUSStore, TCPPrimaryBrandCanadaStore) =>
  !isCanada() ? TCPPrimaryBrandUSStore : TCPPrimaryBrandCanadaStore;

const getTcpStyleType = (TCPStyleTypeUS, TCPStyleTypeCA) =>
  !isCanada() ? TCPStyleTypeUS : TCPStyleTypeCA;
const getTcpStyleQty = (TCPStyleQTYUS, TCPStyleQTYCA) =>
  !isCanada() ? TCPStyleQTYUS : TCPStyleQTYCA;

const renderProductCarousel = (propsVal, currentCatId, productTileRef) => {
  const {
    productTabList,
    icidParam,
    isHomePage,
    onQuickViewOpenClick,
    windowSize,
    bgColor,
    isDamImageV2Enabled,
    isDynamicBadgeEnabled,
    isRatingViewDisabled,
    isColorSwatchesEnabled,
    isNewSwatchUrlEnabled,
    isDiscountPriceDisabled,
    isTabbedATBDisabled,
    isTabbedProductTileBorderDisabled,
    moduleAnalyticsTag,
  } = propsVal;
  const isBgColorPassed = !isEmpty(bgColor);
  const breakPointVal = calculateBreakPointByWidth(windowSize);
  let data = productTabList[currentCatId] || [];
  data = data.slice(0, TOTAL_IMAGES);

  if (data.length > 0) {
    return (
      <Carousel
        key={`product-carousel-${currentCatId}`}
        isArrowVisible={data.length > ARROW_VISIBLE_BP[breakPointVal]}
        tileRef={productTileRef}
        defaultTileWidth={265}
        slidesToScroll={SLIDES_TO_SCROLL_BP[breakPointVal]}
      >
        {data.map((tileData, index) => {
          const {
            pdpUrl,
            pdpAsPath,
            product_name: productName,
            min_offer_price: minOfferPrice,
            min_list_price: minListPrice,
            TCPBazaarVoiceRating,
            TCPBazaarVoiceReviewCount,
            TCPMerchantTagUSStore,
            TCPMerchantTagCanadaStore,
            TCPPrimaryBrandUSStore,
            TCPPrimaryBrandCanadaStore,
            uniqueId,
            TCPStyleTypeUS,
            TCPStyleTypeCA,
            TCPStyleQTYUS,
            TCPStyleQTYCA,
            TCPSwatchesUSStore,
            TCPSwatchesCanadaStore,
            TCPColor: tcpColor,
            swatchimage: swatchImg,
            prodpartno: productPartNo,
            auxdescription: auxDescription,
            related_product_swatch_images: relatedSwatchImages,
          } = tileData;
          const pdpAsPathWithTrackingParams = mergeUrlQueryParams(pdpAsPath, icidParam);
          const refVal = index === 0 ? productTileRef : null;
          const newPdpUrl = pdpUrl && `${pdpUrl}&dataSource=module_G_${currentCatId}`;
          const badge3 = TCPMerchantTagUSStore || TCPMerchantTagCanadaStore || null;
          const badge3Text = minListPrice - minOfferPrice !== 0 ? badge3 : '';
          const primaryBrand = getBrandVal(TCPPrimaryBrandUSStore, TCPPrimaryBrandCanadaStore);
          const tcpStyleType = getTcpStyleType(TCPStyleTypeUS, TCPStyleTypeCA);
          const tcpStyleQty = getTcpStyleQty(TCPStyleQTYUS, TCPStyleQTYCA);
          const dynamicBadgeOverlayQty = getDynamicBadgeQty(
            tcpStyleType,
            tcpStyleQty,
            isDynamicBadgeEnabled
          );

          const isBundle = isBundleProduct(tileData);
          const apiConfig = getAPIConfig();
          const { siteId } = apiConfig;
          const TCPSwatches = siteId === 'us' ? TCPSwatchesUSStore : TCPSwatchesCanadaStore;
          const { assetHost, assetPath } = getImageAssetConfig(
            primaryBrand.toLowerCase(),
            apiConfig
          );

          return (
            <StyledProductWrapper
              key={`product-tile-${index.toString()}-${uniqueId}`}
              ref={refVal}
              className="tile-wrapper"
              isBgColorPassed={isBgColorPassed}
              isTabbedProductTileBorderDisabled={isTabbedProductTileBorderDisabled}
            >
              {renderImage(
                newPdpUrl,
                pdpAsPathWithTrackingParams,
                index,
                uniqueId,
                productName,
                isHomePage,
                primaryBrand,
                isDamImageV2Enabled,
                dynamicBadgeOverlayQty,
                tcpStyleType,
                moduleAnalyticsTag
              )}
              <StyledProductDetails>
                {renderColorSwatches({
                  productPartNo,
                  TCPSwatches,
                  onQuickViewOpenClick,
                  assetHost,
                  assetPath,
                  swatchImg,
                  tcpColor,
                  auxDescription,
                  relatedSwatchImages,
                  isBundle,
                  isNewSwatchUrlEnabled,
                  isColorSwatchesEnabled,
                })}
                {renderPriceSection(
                  minListPrice,
                  minOfferPrice,
                  badge3,
                  badge3Text,
                  uniqueId,
                  productName,
                  onQuickViewOpenClick,
                  isDiscountPriceDisabled,
                  isTabbedATBDisabled
                )}
                {renderProductName(newPdpUrl, pdpAsPathWithTrackingParams, productName)}
                {renderRatings(
                  TCPBazaarVoiceRating,
                  TCPBazaarVoiceReviewCount,
                  isRatingViewDisabled
                )}
              </StyledProductDetails>
            </StyledProductWrapper>
          );
        })}
      </Carousel>
    );
  }
  return <StyledSkeleton col={6} colSize={{ small: 2, medium: 2, large: 2 }} removeLastMargin />;
};

const renderABProductCarousel = (propsVal, currentCatId, productTileRef, contextExtraConfig) => {
  const {
    bgColor,
    icidParam,
    isHomePage,
    onQuickViewOpenClick,
    isDamImageV2Enabled,
    carouselTabData,
    windowSize,
    isDynamicBadgeEnabled,
    isRatingViewDisabled,
    isTabbedATBDisabled,
    isDiscountPriceDisabled,
    isTabbedProductTileBorderDisabled,
    moduleAnalyticsTag,
    isSimilarProducts,
    isPromoMsgEnabledRecommendation,
    isUpdatedPromotionsDisplayEnabled,
    promotionsData,
    serverTime,
    isTransparentImageSizeReduceEnabled,
  } = propsVal;
  const isBgColorPassed = !isEmpty(bgColor);
  const breakPointVal = calculateBreakPointByWidth(windowSize);
  const matchingKey = !isEmpty(currentCatId)
    ? currentCatId?.split(' ')?.join('')?.toLowerCase()
    : '';
  let data = carouselTabData[matchingKey] || [];
  data = data.slice(0, TOTAL_IMAGES);
  const slidesToScrollBp = isSimilarProducts
    ? SLIDES_TO_SCROLL_BP_SIMILAR_STYLES
    : SLIDES_TO_SCROLL_BP;
  if (data.length > 0) {
    return (
      <Carousel
        key={`ab-product-carousel-${currentCatId}`}
        isArrowVisible={data.length > ARROW_VISIBLE_BP[breakPointVal]}
        tileRef={productTileRef}
        defaultTileWidth={265}
        slidesToScroll={slidesToScrollBp[breakPointVal]}
      >
        {data.map((item, index) => {
          const { uniqueId, productInfo } = item || {};
          const {
            ratings,
            reviewsCount,
            pdpUrl,
            primaryBrand,
            name: productName,
            listPrice,
            offerPrice,
            TCPMerchantTagUSStore,
            TCPMerchantTagCanadaStore,
            tcpStyleType,
            tcpStyleQty,
            TCPPromotionPrice,
            TCPPromotionID,
            imageUrl,
          } = productInfo;
          const refVal = index === 0 ? productTileRef : null;
          const pdpAsPathWithTrackingParams = mergeUrlQueryParams(pdpUrl, icidParam);
          const badge3 = TCPMerchantTagUSStore || TCPMerchantTagCanadaStore || null;
          const badge3Text = listPrice - offerPrice !== 0 ? badge3 : '';
          const dynamicBadgeOverlayQty = getDynamicBadgeQty(
            tcpStyleType,
            tcpStyleQty,
            isDynamicBadgeEnabled
          );
          const pdpToPath = getProductListToPath(pdpUrl);
          const isPngImage = /\.png$/i.test(imageUrl);

          const backGroundStyle = getBackGroundStyle(
            contextExtraConfig,
            isTransparentImageSizeReduceEnabled,
            imageUrl
          );

          return (
            <StyledProductWrapper
              key={`product-tile-${index.toString()}-${uniqueId}`}
              ref={refVal}
              className="tile-wrapper"
              isBgColorPassed={isBgColorPassed}
              isTabbedProductTileBorderDisabled={isTabbedProductTileBorderDisabled}
            >
              {renderImage(
                pdpToPath,
                pdpAsPathWithTrackingParams,
                index,
                uniqueId,
                productName,
                isHomePage,
                primaryBrand,
                isDamImageV2Enabled,
                dynamicBadgeOverlayQty,
                tcpStyleType,
                moduleAnalyticsTag,
                isSimilarProducts,
                imageUrl,
                isTransparentImageSizeReduceEnabled,
                isPngImage,
                backGroundStyle
              )}
              <StyledProductDetails>
                {renderPriceSection(
                  listPrice,
                  offerPrice,
                  badge3,
                  badge3Text,
                  uniqueId,
                  productName,
                  onQuickViewOpenClick,
                  isDiscountPriceDisabled,
                  isTabbedATBDisabled
                )}
                {renderProductName(pdpToPath, pdpAsPathWithTrackingParams, productName)}
                {renderRatings(ratings, reviewsCount, isRatingViewDisabled)}
                {isPromoMsgEnabledRecommendation &&
                  isSimilarProducts &&
                  renderPromotions({
                    promotionsData,
                    TCPPromotionPrice,
                    TCPPromotionID,
                    isUpdatedPromotionsDisplayEnabled,
                    serverTime,
                  })}
              </StyledProductDetails>
            </StyledProductWrapper>
          );
        })}
      </Carousel>
    );
  }
  return <StyledSkeleton col={6} colSize={{ small: 2, medium: 2, large: 2 }} removeLastMargin />;
};

const CarouselTab = (props) => {
  const categoryTabRef = useRef(null);
  const productTileRef = useRef(null);
  const [currentCatId, setCurrentCatId] = useState([]);

  const {
    carouselTabs,
    categoriesList,
    productTabList,
    getProductTabListData,
    onProductTabChange,
    windowSize,
    variation,
    fetchRecommendationData,
    carouselTabData,
  } = props;
  let buttonTabItems = [];
  let totalTabs = 0;

  const breakPoint = calculateBreakPointByWidth(windowSize);

  if (variation === ABVariation) {
    buttonTabItems = categoriesList;
    totalTabs = categoriesList?.length;
  } else {
    buttonTabItems = getButtonTabItems(carouselTabs);
    totalTabs = carouselTabs?.length;
  }

  const onTabChange = (catId) => {
    if (variation === ABVariation) {
      setCurrentCatId(catId);
      const categoryName = catId?.toLowerCase()?.split(' ')?.join('');
      const productData = carouselTabData[categoryName];
      getProductsData({ productData, categoryId: catId, fetchRecommendationData });
    } else {
      updateCategoryId(
        catId,
        setCurrentCatId,
        carouselTabs,
        productTabList,
        getProductTabListData,
        onProductTabChange
      );
    }
  };

  const contextExtraConfig = React.useContext(LabelsContext)?.extraConfig || {};

  useEffect(() => {
    if (variation === ABVariation) {
      const categoryId = categoriesList[0]?.id;
      setCurrentCatId(categoryId);
      const categoryName = categoryId?.toLowerCase()?.split(' ')?.join('');
      const productData = carouselTabData[categoryName];
      getProductsData({ productData, categoryId, fetchRecommendationData });
    } else {
      const {
        carouselTabs: [item = {}],
      } = props;
      const { category = [] } = item;
      const processedCatId = getCategoryIds(category);

      if (isClient()) {
        updateCategoryId(
          processedCatId,
          setCurrentCatId,
          carouselTabs,
          productTabList,
          getProductTabListData,
          onProductTabChange
        );
      }
    }
  }, []);

  return (
    <>
      {totalTabs > 1 && (
        <StyledTabSection className="tabs-section">
          <Carousel
            isArrowVisible={totalTabs > ARROW_VISIBLE_BP[breakPoint]}
            tileRef={categoryTabRef}
            defaultTileWidth={265}
            slidesToScroll={SLIDES_TO_SCROLL_BP[breakPoint]}
            isCategoryTab
          >
            {buttonTabItems.map(({ label, id, styledObjWeb }, index) => (
              <Button
                className={`category-tab-${index}`}
                active={getActiveStatus(id, currentCatId)}
                buttonVariation="mini-nav"
                onClick={() => onTabChange(id)}
                {...styledObjWeb}
              >
                {label}
              </Button>
            ))}
          </Carousel>
        </StyledTabSection>
      )}
      <StyledProductSection className="tab-product-section">
        {variation === ABVariation
          ? renderABProductCarousel(props, currentCatId, productTileRef, contextExtraConfig)
          : renderProductCarousel(props, currentCatId, productTileRef)}
      </StyledProductSection>
    </>
  );
};

CarouselTab.propTypes = {
  isHpNewModuleDesignEnabled: PropTypes.bool,
  carouselTabs: PropTypes.arrayOf(PropTypes.shape({})),
  categoriesList: PropTypes.arrayOf(PropTypes.shape({})),
  productTabList: PropTypes.shape({
    completed: PropTypes.shape({}),
  }),
  onProductTabChange: PropTypes.func,
  getProductTabListData: PropTypes.func,
  onQuickViewOpenClick: PropTypes.func,
  icidParam: PropTypes.string,
  isHomePage: PropTypes.bool,
  windowSize: PropTypes.shape({}),
  variation: PropTypes.string,
  fetchRecommendationData: PropTypes.func.isRequired,
  carouselTabData: PropTypes.shape({
    completed: PropTypes.shape({}),
  }),
  isDynamicBadgeEnabled: PropTypes.shape({}).isRequired,
  isSimilarProducts: PropTypes.bool,
  isTransparentImageSizeReduceEnabled: PropTypes.bool,
};

CarouselTab.defaultProps = {
  isHpNewModuleDesignEnabled: false,
  icidParam: '',
  isHomePage: false,
  carouselTabs: [],
  categoriesList: [],
  productTabList: {},
  onProductTabChange: () => {},
  getProductTabListData: () => {},
  onQuickViewOpenClick: () => {},
  windowSize: {},
  variation: '',
  carouselTabData: {},
  isSimilarProducts: false,
  isTransparentImageSizeReduceEnabled: false,
};

CarouselTab.displayName = 'CarouselTab';

export default SafeComponent(CarouselTab);
export { CarouselTab as CarouselTabVanilla };
